import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as XLSX from "xlsx";
import DataGrid from "react-data-grid";
import JsBarcode from "jsbarcode";
import "jspdf-autotable";
import jsPDF from "jspdf";
import { supabase } from "../client/client";
import { useLogin } from "../context/LoginContext";
import Layout from "./Layout";
import Loading from "./Loading";
import DownloadIcon from "./DownloadIcon";
import CreateIcon from "./CreateIcon";
import "react-data-grid/lib/styles.css";
import FetchSoftware from "./FetchSoftware";
import FetchBackups from "./FetchBackups";
import FetchMaintenance from "./FetchMaintenance";
import FetchBackupsServer from "./FetchBackupsServer";
import FetchPasswordChanges from "./FetchPasswordChanges";

const ProductsPage = () => {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({
    title: "",
    brand: "",
    state: "",
    attendant: "",
  });

  const { getSessionAuth } = useLogin();
  const navigate = useNavigate();

  useEffect(() => {
    const sessionAuth = async () => {
      const session = await getSessionAuth();
      if (session) {
        await fetchData();
        setLoading(false);
      } else {
        navigate("/", { replace: true });
      }
    };
    sessionAuth();
  }, [getSessionAuth, navigate]);

  const handleImageClick = (row) => {
    navigate(`/informacion/${row.id}`);
  };

  useEffect(() => {
    const filtered = data.filter((row) => {
      return (
        (row.title    ?? "").toLowerCase().includes(filters.title.toLowerCase()) &&
        (row.brand    ?? "").toLowerCase().includes(filters.brand.toLowerCase()) &&
        (row.state    ?? "").toLowerCase().includes(filters.state.toLowerCase()) &&
        (row.attendant ?? "").toLowerCase().includes(filters.attendant.toLowerCase())
      );
    });
    setFilteredData(filtered);
  }, [data, filters]);

  const handleFilterChange = (filterName, value) => {
    setFilters((prevFilters) => ({ ...prevFilters, [filterName]: value }));
  };

  const columnsHardware = [
    {
      field: "image1",
      name: "Foto",
      key: "image1",
      width: 30,
      renderCell: ({ row }) => (
        <Link to={`/informacion/${row.id}`} onClick={() => handleImageClick(row)}>
          <img className="" src={row.image1} alt={row.title} />
        </Link>
      ),
    },
    { field: "attendant", name: "Encargado", width: 220, key: "attendant" },
    {
      field: "title",
      name: "Nombre",
      width: 300,
      key: "title",
      renderCell: ({ row }) => {
        return (
          <Link to={`/equipos/${row.id}/editar`} underline="always">
            {row.title}
          </Link>
        );
      },
    },
    { field: "brand", name: "Marca", width: 300, key: "brand" },
    { field: "state", name: "Estado", width: 250, key: "state" },
  ];

  const gridStyle = {
    minHeight: '500px',
  };

  const handleDownloadPDF = () => {
    const pdf = new jsPDF({
      unit: 'cm',
      format: [21, 29.7],
    });

    const etiquetaWidth = 6.3;
    const etiquetaHeight = 2.2;

    const rowsPerPage = 11;
    const columnsPerPage = 3;
    const margenIzquierdo = 0.6;
    const separacionHorizontal = 0.5;

    data.forEach((product, index) => {
      if (index > 0 && index % (rowsPerPage * columnsPerPage) === 0) {
        pdf.addPage();
      }

      const colIndex = index % columnsPerPage;
      const rowIndex = Math.floor(index / columnsPerPage) % rowsPerPage;

      const startX = colIndex * (etiquetaWidth + separacionHorizontal) + margenIzquierdo;
      const startY = rowIndex * (etiquetaHeight + 0.4);

      const canvas = document.createElement("canvas");

      JsBarcode(canvas, product.id, { format: "CODE128", fontSize: 30, width: 1.4 });

      const barcodeDataURL = canvas.toDataURL("image/png");
      pdf.addImage(barcodeDataURL, "PNG", startX, startY, etiquetaWidth, etiquetaHeight);

      pdf.setFontSize(6);
      const text = `${product.title} - ${product.area}`;
      pdf.text(text, startX, startY + etiquetaHeight + 0.3);
    });

    pdf.save("etiquetas.pdf");
  };

  const handleDownloadExcel = () => {
    const originalSheet = XLSX.utils.json_to_sheet(data);

    const combinedSheet = XLSX.utils.aoa_to_sheet([
      ["CENTRO DE ARBITRAJE DE LA CÁMARA DE COMERCIO E INDUSTRIA DE AREQUIPA"],
      ["PROCEDIMIENTO"],
      ["SOPORTE DE T.I."],
      ["IT-F-02"],
      ["VERSIÓN: 01"],
      ["FECHA: 26/02/2024"],
      ["REVISA: CONTADOR"],
      ["APRUEBA: GG"],
      ["FICHA DE EQUIPO"],
      [],
      ...XLSX.utils.sheet_to_json(originalSheet, { header: 1 })
    ]);

    XLSX.writeFile({ Sheets: { Combined: combinedSheet }, SheetNames: ['Combined'] }, 'Hardware.xlsx');
  };

  const handleDownloadExcelBackups = async () => {
    try {
      const { data: backupsData, error: backupsError } = await supabase.from("backups").select("*");
      if (backupsError) {
        console.error("Error fetching backup data:", backupsError);
        return;
      }
      const { data: equiposData, error: equiposError } = await supabase.from("equipos").select("id, title, attendant");
      if (equiposError) {
        console.error("Error fetching equipos data:", equiposError);
        return;
      }
      const backupsWithEquiposData = backupsData.map((backup) => {
        const equipo = equiposData.find((e) => e.id === backup.id_equipo);
        return {
          ...backup,
          title_equipo:    equipo?.title    ?? "",
          attendant_equipo: equipo?.attendant ?? "",
        };
      });

      const originalSheet = XLSX.utils.json_to_sheet(backupsWithEquiposData);

      const combinedSheet = XLSX.utils.aoa_to_sheet([
        ["CENTRO DE ARBITRAJE DE LA CÁMARA DE COMERCIO E INDUSTRIA DE AREQUIPA"],
        ["PROCEDIMIENTO"],
        ["SOPORTE DE T.I."],
        ["TI-P-01"],
        ["VERSIÓN: 01"],
        ["FECHA: 26/02/2024"],
        ["REVISA: CONTADOR"],
        ["APRUEBA: GG"],
        [],
        ...XLSX.utils.sheet_to_json(originalSheet, { header: 1 })
      ]);

      XLSX.writeFile({ Sheets: { Combined: combinedSheet }, SheetNames: ['Combined'] }, 'Backups.xlsx');
    } catch (error) {
      console.error("Error fetching and processing data:", error.message);
    }
  };

  const handleDownloadExcelBackupsServer = async () => {
    try {
      const { data: backupsData, error: backupsError } = await supabase.from("backups_server").select("*");
      if (backupsError) {
        console.error("Error fetching backup data:", backupsError);
        return;
      }
      const { data: equiposData, error: equiposError } = await supabase.from("equipos").select("id, title, attendant");
      if (equiposError) {
        console.error("Error fetching equipos data:", equiposError);
        return;
      }
      const backupsWithEquiposData = backupsData.map((backup) => {
        const equipo = equiposData.find((e) => e.id === backup.id_equipo);
        return {
          ...backup,
          title_equipo:    equipo?.title    ?? "",
          attendant_equipo: equipo?.attendant ?? "",
        };
      });

      const originalSheet = XLSX.utils.json_to_sheet(backupsWithEquiposData);

      const combinedSheet = XLSX.utils.aoa_to_sheet([
        ["CENTRO DE ARBITRAJE DE LA CÁMARA DE COMERCIO E INDUSTRIA DE AREQUIPA"],
        ["PROCEDIMIENTO"],
        ["SOPORTE DE T.I."],
        ["TI-P-01"],
        ["VERSIÓN: 01"],
        ["FECHA: 26/02/2024"],
        ["REVISA: CONTADOR"],
        ["APRUEBA: GG"],
        [],
        ...XLSX.utils.sheet_to_json(originalSheet, { header: 1 })
      ]);

      XLSX.writeFile({ Sheets: { Combined: combinedSheet }, SheetNames: ['Combined'] }, 'backupsServidor.xlsx');
    } catch (error) {
      console.error("Error fetching and processing data:", error.message);
    }
  };

  const handleDownloadExcelMaintenance = async () => {
    try {
      const { data: maintenanceData, error: maintenanceError } = await supabase.from("maintenance").select("*");
      if (maintenanceError) {
        console.error("Error fetching maintenance data:", maintenanceError);
        return;
      }
      const { data: equiposData, error: equiposError } = await supabase.from("equipos").select("id, title, attendant");
      if (equiposError) {
        console.error("Error fetching equipos data:", equiposError);
        return;
      }
      const maintenanceWithEquiposData = maintenanceData.map((maintenance) => {
        const equipo = equiposData.find((e) => e.id === maintenance.id_equipo);
        return {
          ...maintenance,
          title_equipo:    equipo?.title    ?? "",
          attendant_equipo: equipo?.attendant ?? "",
        };
      });

      const originalSheet = XLSX.utils.json_to_sheet(maintenanceWithEquiposData);

      const combinedSheet = XLSX.utils.aoa_to_sheet([
        ["CENTRO DE ARBITRAJE DE LA CÁMARA DE COMERCIO E INDUSTRIA DE AREQUIPA"],
        ["PROCEDIMIENTO"],
        ["SOPORTE DE T.I."],
        ["IT-F-03"],
        ["VERSIÓN: 01"],
        ["FECHA: 26/02/2024"],
        ["REVISA: CONTADOR"],
        ["APRUEBA: GG"],
        ["DETALLE DE MANTENIMIENTO"],
        [],
        ...XLSX.utils.sheet_to_json(originalSheet, { header: 1 })
      ]);

      XLSX.writeFile({ Sheets: { Combined: combinedSheet }, SheetNames: ['Combined'] }, 'Mantenimiento.xlsx');
    } catch (error) {
      console.error("Error fetching and processing data:", error.message);
    }
  };

  const handleDownloadExcelSoftware = async () => {
    try {
      const { data: softwareData, error: softwareError } = await supabase.from("licencias").select("*");
      if (softwareError) {
        console.error("Error fetching software data:", softwareError);
        return;
      }
      const { data: equiposData, error: equiposError } = await supabase.from("equipos").select("id, title, attendant");
      if (equiposError) {
        console.error("Error fetching equipos data:", equiposError);
        return;
      }
      const softwareWithEquiposData = softwareData.map((software) => {
        const equipo = equiposData.find((e) => e.id === software.id_equipo);
        return {
          ...software,
          title_equipo:    equipo?.title    ?? "",
          attendant_equipo: equipo?.attendant ?? "",
        };
      });

      const originalSheet = XLSX.utils.json_to_sheet(softwareWithEquiposData);

      const combinedSheet = XLSX.utils.aoa_to_sheet([
        ["CENTRO DE ARBITRAJE DE LA CÁMARA DE COMERCIO E INDUSTRIA DE AREQUIPA"],
        ["PROCEDIMIENTO"],
        ["SOPORTE DE T.I."],
        ["TI-P-01"],
        ["VERSIÓN: 01"],
        ["FECHA: 26/02/2024"],
        ["REVISA: CONTADOR"],
        ["APRUEBA: GG"],
        [],
        ...XLSX.utils.sheet_to_json(originalSheet, { header: 1 })
      ]);

      XLSX.writeFile({ Sheets: { Combined: combinedSheet }, SheetNames: ['Combined'] }, 'Software.xlsx');
    } catch (error) {
      console.error("Error fetching and processing data:", error.message);
    }
  };

  const fetchData = async () => {
    try {
      const { data, error } = await supabase
        .from("equipos")
        .select("*")
        .or(
          "area.eq.ARBITRAJE," +
          "area.eq.TECNOLOGIA DE INFORMACION," +
          "attendant.eq.SALOMÉ GALDOS," +
          "attendant.eq.KATHERINE SUEROS," +
          "attendant.eq.RUTH VARGAS"
        );

      if (error) {
        console.error("Error fetching data:", error);
      } else {
        setData(data);
      }
    } catch (error) {
      console.error("Error fetching data:", error.message);
    }
  };

  const handleTab = (tab) => setActiveTab((prev) => (prev === tab ? null : tab));

  return (
    <Layout>
      {loading && <Loading />}
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="text-[#4691A6] font-bold text-3xl">EQUIPOS</h1>
          <p className="text-gray-500 text-sm mt-1">{data.length} equipo{data.length !== 1 ? "s" : ""} encontrado{data.length !== 1 ? "s" : ""}</p>
        </div>
        <button
          onClick={handleDownloadPDF}
          className="bg-[#356674] hover:bg-[#4691A6] transition-colors duration-200 text-sm cursor-pointer h-9 flex items-center gap-2 text-white px-4 py-2 rounded-lg shadow-sm"
        >
          <DownloadIcon className="w-4 h-4" />
          Etiquetas PDF
        </button>
      </div>
      <p className="text-gray-500 text-sm mt-2 mb-6">
        Haz clic en la imagen para ver el detalle y en el nombre para editar.
      </p>
      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { key: "hardware",   label: "HARDWARE" },
          { key: "software",   label: "SOFTWARE" },
          { key: "backups",    label: "BACKUPS Y MANTENIMIENTO" },
          { key: "bkserver",   label: "BACKUPS SERVIDOR" },
          { key: "passwords",  label: "CAMBIO DE CONTRASEÑAS" },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => handleTab(key)}
            className={`text-sm font-semibold h-10 px-4 rounded-lg transition-colors duration-200 ${
              activeTab === key
                ? "bg-[#356674] text-white"
                : "bg-white border border-gray-200 text-gray-600 hover:border-[#4691A6] hover:text-[#356674]"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <section id="options">
        {activeTab === "hardware" && (
          <>

            <div className="flex items-center gap-2 m-7 ml-0">
              <Link to="/formulario-equipo" className="bg-[#4691A6] hover:bg-[#356674] transition-colors duration-300 text-sm cursor-pointer h-8 flex items-center gap-1 text-white px-2 py-1 rounded-lg">
                <CreateIcon className="w-5" />
                Agregar Hardware
              </Link>
              <button
                onClick={handleDownloadExcel}
                className="bg-[#4691A6] hover:bg-[#356674] transition-colors duration-300 text-sm cursor-pointer h-8 flex items-center gap-1 text-white px-2 py-1 rounded-lg"
              >
                <DownloadIcon className="w-5" />
                Descargar Excel
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 mt-5">
              {[
                { key: "title",    placeholder: "Filtrar por nombre" },
                { key: "brand",    placeholder: "Filtrar por marca" },
                { key: "state",    placeholder: "Filtrar por estado" },
                { key: "attendant", placeholder: "Filtrar por encargado" },
              ].map(({ key, placeholder }) => (
                <input
                  key={key}
                  type="text"
                  placeholder={placeholder}
                  value={filters[key]}
                  onChange={(e) => handleFilterChange(key, e.target.value)}
                  className="px-3 py-2 text-sm rounded-lg border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#4691A6]/40 focus:border-[#4691A6] transition"
                />
              ))}
            </div>
            <DataGrid rows={filteredData} columns={columnsHardware} style={gridStyle} rowHeight={50} minHeight={400} className="rdg-light" />
          </>
        )}
        {activeTab === "software" && (
          <>
            <div className="flex items-center gap-2 m-7 ml-0">
              <Link to="/software/crear" className="bg-[#4691A6] hover:bg-[#356674] transition-colors duration-300 text-sm cursor-pointer h-8 flex items-center gap-1 text-white px-2 py-1 rounded-lg">
                <CreateIcon className="w-5" />
                Agregar Software
              </Link>
              <button
                onClick={handleDownloadExcelSoftware}
                className="bg-[#4691A6] hover:bg-[#356674] transition-colors duration-300 text-sm cursor-pointer h-8 flex items-center gap-1 text-white px-2 py-1 rounded-lg"
              >
                <DownloadIcon className="w-5" />
                Descargar Excel
              </button>
            </div>
            <FetchSoftware />
          </>
        )}
        {activeTab === "mant" && (
          <>
            <div className="flex items-center gap-2 m-7 ml-0">
              <Link to="/mantenimiento/crear" className="bg-[#4691A6] hover:bg-[#356674] transition-colors duration-300 text-sm cursor-pointer h-8 flex items-center gap-1 text-white px-2 py-1 rounded-lg">
                <CreateIcon className="w-5" />
                Agregar Mantenimiento
              </Link>
              <button
                onClick={handleDownloadExcelMaintenance}
                className="bg-[#4691A6] hover:bg-[#356674] transition-colors duration-300 text-sm cursor-pointer h-8 flex items-center gap-1 text-white px-2 py-1 rounded-lg"
              >
                <DownloadIcon className="w-5" />
                Descargar Excel
              </button>
            </div>
            <FetchMaintenance />
          </>)}
        {activeTab === "backups" && (
          <>
            <div className="flex items-center gap-2 m-7 ml-0">
              <Link to={`/backups/crear`} className="bg-[#4691A6] hover:bg-[#356674] transition-colors duration-300 text-sm cursor-pointer h-8 flex items-center gap-1 text-white px-2 py-1 rounded-lg">
                <CreateIcon className="w-5" />
                Agregar backup
              </Link>
              <button
                onClick={handleDownloadExcelBackups}
                className="bg-[#4691A6] hover:bg-[#356674] transition-colors duration-300 text-sm cursor-pointer h-8 flex items-center gap-1 text-white px-2 py-1 rounded-lg"
              >
                <DownloadIcon className="w-5" />
                Descargar Excel
              </button>
            </div>
            <FetchBackups />
          </>
        )}
        {activeTab === "bkserver" && (
          <>
            <div className="flex items-center gap-2 m-7 ml-0">
              <Link to={`/backups-server/crear`} className="bg-[#4691A6] hover:bg-[#356674] transition-colors duration-300 text-sm cursor-pointer h-8 flex items-center gap-1 text-white px-2 py-1 rounded-lg">
                <CreateIcon className="w-5" />
                Agregar backup del servidor
              </Link>
              <button
                onClick={handleDownloadExcelBackupsServer}
                className="bg-[#4691A6] hover:bg-[#356674] transition-colors duration-300 text-sm cursor-pointer h-8 flex items-center gap-1 text-white px-2 py-1 rounded-lg"
              >
                <DownloadIcon className="w-5" />
                Descargar Excel
              </button>
            </div>
            <div>
              <div className="flex gap-2">
                <p className="font-bold">Sistema operativo:</p>
                <p className="font-normal">LINUX</p>
              </div>
              <div className="flex gap-2 pt-5">
                <p className="font-bold">Uso Del Disco:</p>
                <p className="font-normal">126,95 GB / 150 GB (84,63%)</p>
              </div>
            </div>
            <FetchBackupsServer />
          </>
        )}
        {activeTab === "passwords" && (
          <>
            <div className="flex items-center gap-2 m-7 ml-0">
              <Link to="/password-changes/crear" className="bg-[#4691A6] hover:bg-[#356674] transition-colors duration-300 text-sm cursor-pointer h-8 flex items-center gap-1 text-white px-2 py-1 rounded-lg">
                <CreateIcon className="w-5" />
                Agregar cambio de contraseña
              </Link>
            </div>
            <FetchPasswordChanges />
          </>
        )}
      </section>
    </Layout>
  );
};

export default ProductsPage;
